<i18n>
en:
  title: "Regist recipe"
  stepper:
    basic: "Basic"
    materials: "materials"
    steps: "Steps"
  image:
    label: "Image"
    error: "The image cannot be read"
  name:
    label: "Name"
  slogan:
    label: "Slogan"
  explanation:
    label: "Explanation"
  time:
    label: "Cooking time"
    suffix: "minutes"
  material:
    label: "Materials"
    name:
      label "Material name"
    quantity:
      label "Quantity"
    sample1:
      name: "例1）鶏むね肉"
      quantity: "1枚(200g)"
    sample2:
      name: "例2）細ねぎ(刻み)"
      quantity: "適量"
    sample3:
      name: "例3）醤油"
      quantity: "大さじ1 と 大さじ1/2"
    group:
      label: "調味料グループ"
      none:
        label: "未登録"
      icon1: "調味料 ♠"
      icon2: "調味料 ♦"
      icon3: "調味料 ♣"
      icon4: "調味料 ♥"
      icon5: "調味料 ★"
      ok: "OK"
      cancel: "キャンセル"
  step:
    label: "作り方"
    image:
      label: "写真"
    explanation:
      label: "説明"
    sample:
      title: "説明の例）"
      text: |-
        フライパンにサラダ油を入れて中火で熱し、鶏肉、なすを入れて両面こんがりとするまで焼く。
        調味料 ♠ を加えて混ぜながらとろみがつくまで、煮からめる。
  back: "Back"
  next: "Next"
  edit: "Edit"
  submit: "Regist"
  error:
    system: "system error."

ja:
  title: "レシピ登録"
  stepper:
    basic: "基本情報"
    materials: "材料"
    steps: "手順"
  image:
    label: "画像"
    error: "画像が読み取れません"
  name:
    label: "料理名"
  slogan:
    label: "キャッチコピー"
  explanation:
    label: "説明"
  time:
    label: "調理時間"
    suffix: "分"
  material:
    label: "材料"
    name:
      label: "材料名"
    quantity:
      label: "分量"
  back: "戻る"
  next: "次へ"
  edit: "編集する"
  submit: "登録する"
  error:
    system: "システムエラーが発生しました。"
</i18n>

<template lang="pug">
  div.my-6
    v-container
      //- タイトル
      v-row
        v-col(cols="12" align="center")
          h2|{{$t('title')}}
        v-col.pt-0.pb-4(cols="12" align="center")
          v-img(src="@/assets/images/title-underline.png" width="227px" height="24px")

      //- 読込中
      template(v-if="isLoading")
        v-row
          v-col.my-12.pa-12(cols="12")
            v-progress-linear(indeterminate)

      //- 読込失敗
      template(v-else-if="err.load")
        v-row
          v-col.my-12.py-12(cols="12" align="center")
            v-alert.d-inline-block(dense outlined type="error")
              div(v-html="err.load")

      //- 入力フォーム
      template(v-else)
        v-stepper.transparent.none-shadow(alt-labels v-model="step")
          v-stepper-header.px-md-12.px-lg-12.px-xl-12.none-shadow
            v-stepper-step(:step="1" :complete="step > 1")|{{$t('stepper.basic')}}
            v-divider
            v-stepper-step(:step="2" :complete="step > 2")|{{$t('stepper.materials')}}
            v-divider
            v-stepper-step(:step="3" :complete="step > 3")|{{$t('stepper.steps')}}

          v-stepper-items
            //- STEP1
            v-stepper-content.px-0(:step="1")
              v-form.px-md-8.px-lg-10.px-xl-12(onsubmit="return false" v-model="valid.step1")
                v-row
                  v-col(cols="12" sm="10" md="6" xl="4")
                    v-file-input.required(accept="image/*" prepend-icon="fa-image" :label="$t('image.label')" :error="validBasicImage.error" :error-messages="validBasicImage.message" maxlength="255" v-model="forms.image")
                v-row
                  v-col.pl-10.pt-0.pb-6(v-if="forms.imagePreview" cols="12" sm="10" md="6" xl="4")
                    v-img(:src="forms.imagePreview" max-width="220px")

                v-row
                  v-col(cols="12" sm="10" md="6" xl="4")
                    v-text-field.required(prepend-icon="fa-comment-alt" :label="$t('slogan.label')" :rules="v($t('slogan.label'), ['required'])" maxlength="50" v-model="forms.slogan")
                  v-col(cols="12" sm="10" md="6" xl="4")
                    v-text-field.required(prepend-icon="fa-signature" :label="$t('name.label')" :rules="v($t('name.label'), ['required'])" maxlength="32" v-model="forms.name")
                  v-col(cols="12")
                    v-textarea.required(prepend-icon="fa-info-circle" :label="$t('explanation.label')" :rules="v($t('explanation.label'), ['required'])" maxlength="128" v-model="forms.explanation")
                  v-col(cols="12" sm="6" md="4" xl="2")
                    v-text-field.required(prepend-icon="fa-user-clock" :label="$t('time.label')" :suffix="$t('time.suffix')" :rules="v($t('time.label'), ['required', 'number'])" maxlength="3" v-model.number="forms.time")

                //- 次へ
                v-row.mt-12(justify="center")
                  v-col(cols="12" sm="4" md="3" xl="2")
                    v-btn(x-large block type="submit" color="primary" @click="nextStep(1)" :disabled="!valid.step1")|{{$t('next')}}

            //- STEP2
            v-stepper-content.px-0(:step="2")
              v-form.px-md-8.px-lg-10.px-xl-12(onsubmit="return false" v-model="valid.step2")
                v-row
                  v-col(cols="12" align-self="center")
                    v-icon|fa-carrot
                    span.ml-3|{{$t('material.label')}}

                v-row
                  v-col.pt-0(cols="12" sm="12" md="8" xl="6")
                    div.ml-12.d-flex.flex-row.blue--text.text--lighten-2(v-for="i of 3" :key="i")
                      div|{{$t(`material.sample${i}.name`)}}
                      v-divider.align-self-center.mx-3
                      div|{{$t(`material.sample${i}.quantity`)}}

                v-row(v-for="(item, index) in forms.materials" :key="`material-${index}`")
                  v-col(cols="12" sm="8" md="6" xl="4")
                    v-text-field.ml-4.required(dense prepend-icon="fa-apple-alt" :label="$t('material.name.label')" :rules="v($t('material.name.label'), ['required'])" maxlength="16" v-model="item.name")
                  v-col(cols="12" sm="4" md="2" xl="2")
                    v-text-field.ml-12.ml-sm-0.required(dense :label="$t('material.quantity.label')" :rules="v($t('material.quantity.label'), ['required'])" maxlength="16" v-model="item.quantity")

                v-row
                  v-col.ml-8.pt-0(cols="12" align-self="center")
                    v-btn.white--text(fab small color="primary" elevation="1" @click="removematerials" :disabled="forms.materials.length <= 1")
                      v-icon|mdi-minus
                    v-btn.ml-2.white--text(fab small color="primary" elevation="1" @click="addMaterials" :disabled="forms.materials.length >= 10")
                      v-icon|mdi-plus

                v-row.mt-7
                  v-col(cols="12" align-self="center")
                    v-icon|fa-folder
                    span.ml-3|{{$t('material.group.label')}}

                v-row
                  v-col(cols="12" v-for="i of 5" :key="i")
                    v-card.ml-4.mr-1
                      v-card-title.pb-0.blue--text|{{$t(`material.group.icon${i}`)}}
                      v-card-text.py-0
                        v-row
                          v-col(v-if="forms.materialGroups[i-1].length === 0" cols="12")
                            |{{$t('material.group.none.label')}}
                          v-col.py-1(v-else v-for="(item, j) in forms.materialGroups[i-1]" cols="12" sm="10" md="8" xl="6" :key="`material-group-${i}-${j}`")
                            div.d-flex.flex-row
                              div|{{item.name}}
                              v-divider.align-self-center.mx-3
                              div|{{item.quantity}}
                          v-col(cols="12" align="right")
                            v-btn(color="primary" @click.stop="openDialog(i)")|{{$t('edit')}}

                //- 次へ
                v-row.mt-12(justify="center")
                  v-col(cols="12" sm="4" md="3" xl="2")
                    v-btn(x-large block color="secondary" @click="nextStep(-1)")|{{$t('back')}}
                  v-col(cols="12" sm="4" md="3" xl="2")
                    v-btn(x-large block type="submit" color="primary" @click="nextStep(1)" :disabled="!valid.step2")|{{$t('next')}}

            //- STEP3
            v-stepper-content.px-0(:step="3")
              v-form.px-md-8.px-lg-10.px-xl-12(onsubmit="return false" v-model="valid.step3")
                v-row
                  v-col(cols="12" align-self="center")
                    v-icon|fa-book-open
                    span.ml-3|{{$t('step.label')}}

                v-row
                  v-col.pt-0(cols="12")
                    div.ml-12.blue--text.text--lighten-2
                      div|{{$t('step.sample.title')}}
                      div.pl-2.recipe-explanation(v-html="$t('step.sample.text')")

                v-row(v-for="(item, index) in forms.steps" :key="`steps-${index}`")
                  v-col(cols="12")
                    v-card.ml-4.mr-1
                      v-card-title.pb-0.blue--text.text--lighten-2|{{index + 1}}.
                      v-card-text.py-0
                        v-row
                          v-col(cols="12" sm="10" md="6" xl="4")
                            v-file-input.required(accept="image/*" prepend-icon="fa-image" :label="$t('image.label')" :error="validStepsImage[index].error" :error-messages="validStepsImage[index].message" maxlength="255" v-model="item.image")
                        v-row
                          v-col.pl-10.pt-0.pb-6(v-if="item.imagePreview" cols="12" sm="10" md="6" xl="4")
                            v-img(:src="item.imagePreview" max-width="220px")
                        v-row
                          v-col(cols="12")
                            v-textarea.required(prepend-icon="fa-info-circle" :label="$t('explanation.label')" :rules="v($t('explanation.label'), ['required'])" maxlength="128" v-model="item.explanation")
                v-row
                  v-col.ml-8.pt-0(cols="12" align-self="center")
                    v-btn.white--text(fab small color="primary" elevation="1" @click="removeSteps" :disabled="forms.steps.length <= 1")
                      v-icon|mdi-minus
                    v-btn.ml-2.white--text(fab small color="primary" elevation="1" @click="addSteps" :disabled="forms.steps.length >= 50")
                      v-icon|mdi-plus

                //- 登録
                v-row.mt-12(justify="center")
                  v-col(cols="12" sm="4" md="3" xl="2")
                    v-btn(x-large block color="secondary" @click="nextStep(-1)")|{{$t('back')}}
                  v-col(cols="12" sm="4" md="3" xl="2")
                    v-btn(x-large block type="submit" color="primary" @click="regist" :loading="isSubmit" :disabled="!valid.step3")|{{$t('submit')}}

                //- エラーメッセージ
                v-row(v-if="err.submit")
                  v-col(cols="12" align="center")
                    v-alert.d-inline-block(dense outlined type="error")
                      div(v-html="err.submit")

    //- 調味料グループ登録ダイアログ
    v-dialog(scrollable persistent max-width="900px" v-model="showDialog")
      v-card
        v-form(onsubmit="return false" v-model="valid.group")
          v-card-title.blue--text|{{$t(`material.group.icon${showDialogIndex}`)}}
          v-divider
          v-card-text
            v-row(v-if="forms.materialGroupEdit.length === 0")
              v-col(cols="12" sm="8" md="6" xl="4")
                strong|{{$t('material.group.none.label')}}

            v-row(v-else v-for="(item, i) in forms.materialGroupEdit" :key="`material-group-edit-${i}`")
              v-col(cols="12" sm="8" md="6" xl="4")
                v-text-field.ml-4.required(dense prepend-icon="fa-apple-alt" :label="$t('material.name.label')" :rules="v($t('material.name.label'), ['required'])" maxlength="16" v-model="item.name")
              v-col(cols="12" sm="4" md="2" xl="2")
                v-text-field.ml-12.ml-sm-0.required(dense :label="$t('material.quantity.label')" :rules="v($t('material.quantity.label'), ['required'])" maxlength="16" v-model="item.quantity")

            v-row
              v-col(cols="12" align-self="center")
                v-btn.white--text(fab small color="primary" elevation="1" @click="removematerialGroupEdit" :disabled="forms.materialGroupEdit.length <= 0")
                  v-icon|mdi-minus
                v-btn.ml-2.white--text(fab small color="primary" elevation="1" @click="addmaterialGroupEdit" :disabled="forms.materialGroupEdit.length >= 50")
                  v-icon|mdi-plus
          v-divider
          v-card-actions
            v-btn(color="secondary" @click="closeDialog(false)")|{{$t('material.group.cancel')}}
            v-spacer
            v-btn(type="submit" color="primary" @click="closeDialog(true)" :disabled="!valid.group")|{{$t('material.group.ok')}}

</template>

<script>
export default {
  data () {
    return {
      // 新規登録（false: 編集）
      newEntry: false,
      // レシピID
      recipeId: null,
      // 入力フォーム
      forms: {
        image: null,
        imagePreview: null,
        imageData: null,
        slogan: null,
        name: null,
        explanation: null,
        time: null,
        materials: [],
        materialGroups: [[], [], [], [], []],
        materialGroupEdit: [],
        steps: []
      },
      // 入力バリデーション
      valid: {
        step1: false,
        step2: false,
        step3: false,
        group: false
      },
      // ステップ
      step: 1,
      // ダイアログの表示
      showDialog: false,
      // 表示中のダイアログの番号
      showDialogIndex: 0,
      // 送信中
      isSubmit: false,
      // 読込中
      isLoading: false,
      // エラー
      err: {
        load: null,
        submit: null
      }
    }
  },
  created () {
    this.recipeId = this.utils.cast.int(this.$route.params.id)
    if (this.recipeId === null) {
      this.newEntry = true
      this.addSteps()
    } else {
      this.loadRecipe()
    }
    this.addMaterials()
  },
  computed: {
    validBasicImage () {
      return this.validImage(this.forms)
    },
    validStepsImage () {
      return Array.from(this.forms.steps, item => this.validImage(item))
    }
  },
  watch: {
    'forms.image' (val) {
      this.readImageFile(val, this.forms)
    }
  },
  methods: {
    /**
      * レシピを読込
      */
    loadRecipe () {
      this.isLoading = true
      this.err.load = this.$t('error.system')

      this.api.shopRecipe({
        recipeId: this.recipeId
      })
        .then(response => {
          this.forms.imagePreview = `data:image/png;base64,${response.data.imageData}`
          this.forms.imageData = response.data.imageData
          this.forms.slogan = response.data.slogan
          this.forms.name = response.data.name
          this.forms.explanation = response.data.explanation
          this.forms.time = response.data.cookingTime
          this.forms.materials = response.data.materials
          this.forms.materialGroups = Array.from(this.forms.materialGroups, (item, index) => response.data.materialGroups[index] || [])
          response.data.steps.forEach(item => this.addSteps())
          this.forms.steps
            .forEach((item, index) => {
              item.imagePreview = `data:image/png;base64,${response.data.steps[index].imageData}`
              item.imageData = response.data.steps[index].imageData
              item.explanation = response.data.steps[index].explanation
            })

          this.err.load = null
        })
        .catch(response => {
          this.err.load = this.$t(`error.${response.functionError.errorCode}`)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    /**
      * 登録
      */
    regist () {
      if (this.isSubmit) {
        return
      }

      this.isSubmit = true
      this.err.submit = null

      this.api.shopRecipeRegist({
        recipeId: this.newEntry ? null : this.recipeId,
        name: this.forms.name,
        slogan: this.forms.slogan,
        explanation: this.forms.explanation,
        cookingTime: this.forms.time,
        imageData: this.forms.imageData,
        materials: this.forms.materials,
        materialGroups: this.forms.materialGroups.filter(item => item.length > 0),
        steps: this.forms.steps
      })
        .then(response => {
          this.$router.push({name: 'ShopComplete'})
        })
        .catch(response => {
          this.err.submit = this.$t(`error.${response.functionError.errorCode}`)
        })
        .finally(() => {
          this.isSubmit = false
        })
    },
    /**
     * 次のSTEPに移動
     */
    nextStep (value) {
      this.step += value
      window.scrollTo(0, 0)
    },
    /**
     * 画像入力の検証
     */
    validImage (item) {
      if (!item.image || item.imageData !== 'error') {
        return {
          error: false,
          message: null
        }
      } else {
        return {
          error: true,
          message: this.$t('image.error')
        }
      }
    },
    /**
     * 画像ファイルを読込
     */
    readImageFile (image, item) {
      if (image) {
        const reader = new FileReader()
        reader.addEventListener('load', () => {
          if (reader.result && reader.result.length > 5) {
            item.imagePreview = reader.result
            item.imageData = this.utils.image.removeMime(reader.result)
          } else {
            item.imagePreview = null
            item.imageData = 'error'
          }
        })
        reader.readAsDataURL(image)
      } else {
        item.imagePreview = null
        item.imageData = null
      }
    },
    /**
     * 材料を追加
     */
    addMaterials () {
      if (this.forms.materials.length >= 50) {
        return
      }
      this.forms.materials.push(
        {
          name: null,
          quantity: null
        }
      )
    },
    /**
     * 材料を削除
     */
    removematerials () {
      if (this.forms.materials.length <= 1) {
        return
      }
      this.forms.materials.pop()
    },
    /**
     * 材料グループを追加
     */
    addmaterialGroupEdit () {
      if (this.forms.materialGroupEdit.length >= 5) {
        return
      }
      this.forms.materialGroupEdit.push(
        {
          name: null,
          quantity: null
        }
      )
    },
    /**
     * 材料グループを削除
     */
    removematerialGroupEdit () {
      if (this.forms.materialGroupEdit.length <= 0) {
        return
      }
      this.forms.materialGroupEdit.pop()
    },
    /**
     * 作り方を追加
     */
    addSteps (defaultData) {
      if (this.forms.steps.length >= 50) {
        return
      }

      const item = {
        image: null,
        imagePreview: null,
        imageData: null,
        explanation: null,
        unwatch: null
      }
      this.forms.steps.push(item)
      item.unwatch = this.$watch(() => item.image, (val) => this.readImageFile(val, item))
    },
    /**
     * 作り方を削除
     */
    removeSteps () {
      if (this.forms.steps.length <= 1) {
        return
      }
      this.forms.steps[this.forms.steps.length - 1].unwatch()
      this.forms.steps.pop()
    },
    /**
     * ダイアログを開く
     */
    openDialog (index) {
      this.forms.materialGroupEdit = this.utils.array.clone(this.forms.materialGroups[index - 1])
      this.showDialogIndex = index
      this.showDialog = true
    },
    /**
     * ダイアログを閉じる
     */
    closeDialog (isSave) {
      if (isSave) {
        this.forms.materialGroups[this.showDialogIndex - 1] = this.forms.materialGroupEdit
      }

      this.showDialog = false
    }
  }
}
</script>

<style lang="sass" scoped>
.none-shadow
  box-shadow: none

.recipe-explanation
  white-space: pre-line
</style>
